import _ from "lodash";
import React, { useState, useEffect, useRef, useMemo } from "react";
import ActionItems from "./ActionItems";
import AudioRecorderSelector from "UIComponents/AudioRecorderSelector";
import I18nHOC from "UIComponents/I18nHOC";
import LinkWithTooltip from "UIComponents/LinkWithTooltip";
import MediaModal from "UIComponents/MediaModal";
import { getAIResponse } from "modules/Services";
import { getDefaultStudentNameOrderPreference } from "modules/CommonSelectors";
import { CreateDropDown } from "Attachment";
import { generateRandomId, useCallbackRef, onEnterOrSpaceKeyDown } from "Utils";
import PropTypes from "prop-types";
import ACLStore from "lib/aclStore";
import { IconButton, ChatBar, EmojiPicker } from "@toddle-edu/ds-web";
import {
  AddOutlined,
  MicrophoneOutlined,
  SmileyHappyOutlined,
  LinkOutlined,
  PlaceholderFileOutlined,
  GoogleDriveColored,
  OneDriveColored,
  CameraOutlined,
  VideoOutlined,
} from "@toddle-edu/ds-icons";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import classes from "./ChatbarWrapper.scss";
import { showNewTheatreMode } from "Utils/GetTheatreModeType";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import classNames from "classnames";
import { Trans } from "react-i18next";
import { setDraftMessageData } from "AppComponents/ChatContainer/modules/ChatContainerModule";
import { ChatPanel } from "@toddle-edu/doc-editor";
import { STUDENT } from "Constants/stringConstants";
import StudentAlerts from "AppComponents/StudentAlerts";

const DROP_DOWN_OPTIONS_FAMILY_MESSAGING = [
  {
    label: "common:capture",
    key: "CAPTURE",
    isItemGroup: true,
    options: [
      {
        key: "capture_image",
        label: "common:image",
        icon: <CameraOutlined variant={"subtle"} />,
      },
      {
        key: "capture_video",
        label: "common:video",
        icon: <VideoOutlined variant={"subtle"} />,
      },
    ],
  },
  {
    label: "common:add",
    key: "ADD",
    isItemGroup: true,
    options: [
      {
        key: "add_link",
        label: "common:link",
        icon: <LinkOutlined variant={"subtle"} />,
      },
    ],
  },
  {
    label: "common:upload",
    key: "UPLOAD",
    isItemGroup: true,
    options: [
      {
        key: "upload_device",
        label: "common:file_from_device",
        icon: <PlaceholderFileOutlined variant={"subtle"} />,
      },
      {
        key: "add_google_drive",
        label: "common:google_drive",
        icon: <GoogleDriveColored />,
      },
      {
        key: "add_one_drive",
        label: "common:one_drive",
        icon: <OneDriveColored />,
      },
    ],
  },
];

const DROP_DOWN_OPTIONS = [
  {
    label: "common:upload",
    key: "UPLOAD",
    isItemGroup: true,
    options: [
      {
        key: "upload_device",
        label: "common:file_from_device",
        icon: <PlaceholderFileOutlined variant={"subtle"} />,
      },
      {
        key: "add_google_drive",
        label: "common:google_drive",
        icon: <GoogleDriveColored />,
      },
      {
        key: "add_one_drive",
        label: "common:one_drive",
        icon: <OneDriveColored />,
      },
    ],
  },
  {
    key: "add_link",
    label: "add_link",
    icon: <LinkOutlined variant={"subtle"} />,
  },
];

const buttonDropDownContainerStyle = {
  top: -235,
};

const buttonDropDownContainerStyleV2 = {
  top: -184,
};

const audioRecorderSelectorStyles = {
  innerContainerStyle: { maxWidth: "1024px" },
};

const stripContainerStyles = {
  height: "56px",
  top: "unset",
};

const getEmptyConversation = () => ({
  id: generateRandomId(),
  label: "",
  attachments: [],
});

const getDropdownOptions = ({ parentType }) => {
  if (parentType == "CHAT") return DROP_DOWN_OPTIONS_FAMILY_MESSAGING;
  return DROP_DOWN_OPTIONS;
};

const MediaModalWrapper = React.memo(props => {
  const { toggleMediaModal, attachments, activeIndex = 0 } = props;
  return (
    <MediaModal
      mode={"view"}
      toggleMediaModal={toggleMediaModal}
      attachments={attachments}
      activeIndex={activeIndex}
      showWorkBook={false}
      showPdfTronEditButton={false}
      showPdfTronfeedback={false}
    />
  );
});
MediaModalWrapper.displayName = "MediaModalWrapper";

const STABLE_EMPTY_OBJECT = {};

const ChatbarWrapper = React.memo(props => {
  const {
    mode,
    messageData,
    sendMessage,
    t,
    dsVersion = "2.0",
    conversationLabel,
    isEmojiEnabled,
    emojiPickerProps = {},
    isTaggingEnabled,
    footerMsgContainerV2,
    collaborators = [],
    isUploading,
    defaultStudentNameOrderPreference,
    isPinMessageLoading,
    uploadFileType,
    parentType,
    cancelEditMessage,
    inputTextPlaceHolder,
    showAddButton = true,
    focus = false,
    onResponseChange,
    selectDropDownOptions,
    onDropDownOptionClick,
    onAddAttachment,
    onDeleteMediaClick,
    onMediaClick,
    onBlur,
    customDropdownRef,
    isStickyContainer,
    isAudioRecordingEnabled,
    shouldExecuteMessageDataUseEffect,
    renderAIActionItems,
    showAccessibilityHelperText = true,
    allowInitialLabelValue,
    replyContainer,
    getAttachmentPopupContainer,
    sendOnEnter = false,
    chatBarScrollParent,
    containerStyle,
    // enableChatPanel props
    getAIResponse,
    getAIResponseWrapper = null, // in case the parent component wants to pass any wrapper around getAIResponse
    enableChatPanel = false,
    prompts,
    onExternalOptionClick,
    plugins,
    translateLanguages,
    metaData,
    feedbackStrip,
    chatPanelRef, // required to ref the enableChatPanel, when boolean is true
    testIdPrefix,
    saveDraftMessage,
    setDraftMessageData,
    draftMessageStoreKey,
    draftMessageData,
    hideGlobalTrayOnCapture = true,
    createDropDownOverrideClasses = STABLE_EMPTY_OBJECT,
    chatBarContainerStyle,
    alwaysShowHelperText = false,
    modalDimensions,
    showNewDesignForCaptureOptions = false,
  } = props;

  const chatBarV2Props = {
    enableChatPanel,
    plugins,
  };
  const customChatbarProps = {
    prompts,
    onExternalOptionClick,
    translateLanguages,
    feedbackStrip,
    metaData,
    getAIResponse: _.isFunction(getAIResponseWrapper)
      ? getAIResponseWrapper
      : getAIResponse,
  };

  const [conversation, onUpdateConversation] = useState(messageData);

  const [initialDraftMessageData, setInitialDraftMessageData] = useState(
    saveDraftMessage ? draftMessageData : getEmptyConversation()
  );

  const [uploadingStatus, setUploadingStatus] = useState(false);
  const [sendingStatus, setSendingStatus] = useState(false);
  const [audioRecorderNodeRef, setAudioRecorderNodeRef] = useCallbackRef();
  const [isPickerOpen, togglePicker] = useState(false);

  const inputRef = useRef();
  const createDropDownRef = useRef();

  const chatBarRef = useRef({ handleInsert: s => {} });

  const [active, setActive] = useState(false);

  const [isChatbarVisible, setChatbarVisible] = useState(true);

  const onMentionActive = active => {
    setActive(active);
  };

  useEffect(() => {
    if (mode === "EDIT" && saveDraftMessage) {
      handleSaveDraftMessage.cancel?.();
      setDraftMessageData({ [draftMessageStoreKey]: {} });
      setInitialDraftMessageData(getEmptyConversation());
    }
  }, [mode]);

  useEffect(() => {
    // this condition is added to prevent data loss issue from local state of this component
    // due to rerender of parent component while creating audio
    if (shouldExecuteMessageDataUseEffect) {
      onUpdateConversation(messageData);
    }
  }, [messageData]);

  useEffect(() => {
    if (saveDraftMessage) {
      setTimeout(() => {
        setInitialDraftMessageData(draftMessageData);
        onUpdateConversation(draftMessageData);
      });
    }
  }, [saveDraftMessage, draftMessageStoreKey]);

  const handleSaveDraftMessage = useMemo(
    () =>
      _.debounce(params => {
        if (mode === "VIEW" && saveDraftMessage && !_.isEmpty(params)) {
          setDraftMessageData({ [draftMessageStoreKey]: params });
        }
      }, 500),
    [mode, saveDraftMessage, setDraftMessageData, draftMessageStoreKey]
  );

  const onEnter = async e => {
    const tempConversation = conversation;
    try {
      if (!uploadingStatus) {
        setSendingStatus(true);

        if (saveDraftMessage && mode !== "EDIT") {
          handleSaveDraftMessage.cancel?.();
          setDraftMessageData({ [draftMessageStoreKey]: {} });
        }

        await sendMessage({
          ...conversation,
        });
        onUpdateConversation(getEmptyConversation());
      }
    } catch {
      onUpdateConversation(tempConversation);
    } finally {
      setSendingStatus(false);
    }
  };

  const inputTextFocusOnDrop = () => {
    inputRef.current && inputRef.current.focus
      ? inputRef.current.focus()
      : null;
  };

  const dropDownOptions = selectDropDownOptions
    ? selectDropDownOptions
    : getDropdownOptions({ parentType });

  const handleAddAttachment = (params, uploadId) => {
    const newAttachments = _.map(params, attachment => {
      return { ...attachment, id: uploadId };
    });

    if (onAddAttachment) {
      onAddAttachment({ ["attachments"]: newAttachments });
      return;
    }
    onUpdateConversation(prev => {
      return {
        ...prev,
        attachments: [...prev.attachments, ...newAttachments],
      };
    });

    if (saveDraftMessage) {
      handleSaveDraftMessage({
        ...conversation,
        attachments: [...conversation.attachments, ...newAttachments],
      });
    }
  };

  const handleAudioRecording = params => {
    if (onAddAttachment) {
      onAddAttachment({
        ["attachments"]: [params["voiceInstruction"]],
      });
      return;
    }
    onUpdateConversation(prev => {
      return {
        ...prev,
        attachments: [
          ..._.get(prev, "attachments", []),
          params["voiceInstruction"],
        ],
      };
    });

    if (saveDraftMessage) {
      handleSaveDraftMessage({
        ...conversation,
        attachments: [
          ..._.get(conversation, "attachments", []),
          params["voiceInstruction"],
        ],
      });
    }
  };

  const renderAddButton = ({ showTooltip = false }) => {
    const buttonComponent = (
      <IconButton
        icon={<AddOutlined />}
        _legacy_variant="plain"
        type="plain"
        variant="neutral"
        size={"small"}
        className={"relative"}
        aria-label="Add Attachment"
        testId={testIdPrefix ? `${testIdPrefix}-addAttachment` : undefined}
      />
    );

    return (
      <CreateDropDown
        ref={createDropDownRef}
        dropDownOptions={dropDownOptions}
        buttonDropDownContainerStyle={
          footerMsgContainerV2
            ? buttonDropDownContainerStyleV2
            : buttonDropDownContainerStyle
        }
        showNewDesignForCaptureOptions={showNewDesignForCaptureOptions}
        overrideClasses={createDropDownOverrideClasses}
        hideGlobalTrayOnCapture={hideGlobalTrayOnCapture}
        isNewDropdown={true}
        zoomPromptText={conversationLabel}
        buttonComponent={
          showTooltip ? (
            <LinkWithTooltip
              tooltip={t("classRoom:attach_files")}
              placement={"bottom"}
              href={null}
            >
              {buttonComponent}
            </LinkWithTooltip>
          ) : (
            buttonComponent
          )
        }
        exportAsPdf={true}
        parentType={"CONVERSATION"}
        parentId={conversation.id}
        updateLoadingState={setUploadingStatus}
        addAttachment={handleAddAttachment}
        inputTextFocusOnDrop={inputTextFocusOnDrop}
        onDropDownOptionClick={onDropDownOptionClick}
        customRef={customDropdownRef}
        disabled={isUploading}
        getNewDropdownPopupContainer={getAttachmentPopupContainer}
        dsVersion={dsVersion}
        modalDimensions={modalDimensions}
      />
    );
  };

  const renderAudioRecorderComponent = () => {
    if (footerMsgContainerV2) {
      stripContainerStyles.height = "48px";
    }

    return isAudioRecordingEnabled ? (
      <div>
        <AudioRecorderSelector
          name="voiceInstruction"
          parentType="CONVERSATION"
          parentId={conversation.id}
          showValue={false}
          setChatbarVisible={setChatbarVisible}
          header={t("common:add_voice_message")}
          version={
            ACLStore.can("FeatureFlag:ShowAudioRecordVisualizer")
              ? "visualiser"
              : undefined
          }
          updateInputField={handleAudioRecording}
          buttonComponent={
            <IconButton
              icon={<MicrophoneOutlined />}
              _legacy_variant={"plain"}
              type="plain"
              variant="neutral"
              size={"small"}
              disabled={isUploading}
              aria-label={t("common:record_audio")}
              testId={testIdPrefix ? `${testIdPrefix}-microphone` : undefined}
            />
          }
          container={audioRecorderNodeRef}
          {...audioRecorderSelectorStyles}
          stripContainerStyles={stripContainerStyles}
          disabled={isUploading}
        />
      </div>
    ) : null;
  };

  const renderEmojiComponent = () => {
    const showEmoji = ACLStore.can("Common:ShowEmoji");
    return (
      isEmojiEnabled &&
      showEmoji && (
        <div>
          <EmojiPicker
            placement="top"
            getPopupContainer={() => document.body}
            {...emojiPickerProps}
            /**
             * isOpen, onOpenChange & onEmojiSelect
             * should not be overridden from emojiPickerProps
             */
            isOpen={isPickerOpen}
            onEmojiSelect={e => {
              const emoji = _.get(e, "native", "");
              if (enableChatPanel) {
                const handleInsert = _.get(
                  chatPanelRef,
                  "current.handleInsert",
                  _.noop
                );
                handleInsert(emoji);
              } else chatBarRef.current.handleInsert(emoji);
            }}
            onOpenChange={open => togglePicker(open)}
          >
            <IconButton
              icon={<SmileyHappyOutlined />}
              _legacy_variant={"plain"}
              type="plain"
              variant="neutral"
              size={"small"}
              isActivated={isPickerOpen}
              onKeyDown={e =>
                onEnterOrSpaceKeyDown(e, () => togglePicker(open => !open))
              }
              testId={testIdPrefix ? `${testIdPrefix}-emoji` : undefined}
              aria-label="Insert Emoji"
            />
          </EmojiPicker>
        </div>
      )
    );
  };

  const [showMediaModal, setShowMediaModal] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState([]);
  const [selectedAttachmentIndex, setSelectedAttachmentIndex] = useState(0);
  const toggleMediaModal = () => {
    setShowMediaModal(!showMediaModal);
  };

  const collabs = [];
  for (var i = 0; i < collaborators.length; i++) {
    const collaboratorType = _.get(collaborators, `${i}.type`, "");
    const displayName = getFormattedUserName({
      user: collaborators[i],
    });

    const labelSuffix =
      collaboratorType === STUDENT ? (
        <StudentAlerts
          studentId={collaborators[i].id}
          alertTypes={_.get(collaborators[i], "alertTypes", [])}
        />
      ) : undefined;

    collabs.push({
      id: collaborators[i].id,
      display: displayName,
      role: collaborators[i].designation?.roles || [],
      profile: collaborators[i].profileImage,
      labelSuffix,
    });
  }

  const updateInputField = params => {
    if (onResponseChange) onResponseChange(params);
    onUpdateConversation(prev => {
      return { ...prev, ...params };
    });

    if (saveDraftMessage) {
      handleSaveDraftMessage({
        ...conversation,
        ...params,
      });
    }
  };

  const [showHelperText, setShowHelperText] = useState(false);

  // show the helper text only when the user has typed something
  useEffect(() => {
    if (
      _.get(conversation, "label", "").length > 0 ||
      conversation.attachments?.length > 0
    ) {
      setShowHelperText(true);
    } else setShowHelperText(false);
  }, [conversation]);

  const deleteAttachment = id => {
    const currentAttachments = conversation.attachments;
    const filteredAttachments = currentAttachments.filter(a => a.id !== id);
    // If we call this function it will cause re-render and automatically update the attachments
    if (onDeleteMediaClick) onDeleteMediaClick({ id });
    else updateInputField({ ["attachments"]: filteredAttachments });
  };

  const onClickMediaCard = attachment => {
    if (onMediaClick) {
      onMediaClick(attachment);
      return;
    }
    const index = _.findIndex(
      _.get(conversation, "attachments", []),
      currentAttachment => _.isEqual(currentAttachment?.url, attachment?.url)
    );
    if (showNewTheatreMode() && index != -1) setSelectedAttachmentIndex(index);
    setSelectedAttachment([
      ...(showNewTheatreMode()
        ? _.get(conversation, "attachments", [])
        : [attachment]),
    ]);
    setShowMediaModal(true);
  };
  const containerClasses = classNames(classes.container, {
    [classes.nonStickyContainer]: !isStickyContainer,
  });

  return (
    <div
      ref={setAudioRecorderNodeRef}
      data-test-id={`${testIdPrefix}-chatbarcontainer`}
      className={containerClasses}
      style={chatBarContainerStyle}
    >
      <ChatBar
        dsVersion={dsVersion}
        mode={_.lowerCase(mode) === "edit" ? "edit" : "view"}
        attachments={[...conversation.attachments]}
        ref={chatBarRef}
        onDeleteClick={deleteAttachment}
        initialValue={
          _.lowerCase(mode) == "edit" || allowInitialLabelValue
            ? conversation.label
            : saveDraftMessage
            ? _.get(initialDraftMessageData, "label", "")
            : ""
        }
        isVisible={isChatbarVisible}
        scrollTarget={chatBarScrollParent}
        suggestionsPortalHost={chatBarScrollParent}
        actionItems={
          <ActionItems
            renderAddButton={showAddButton ? renderAddButton : () => {}}
            renderEmojiComponent={renderEmojiComponent}
            isTaggingEnabled={isTaggingEnabled}
            renderAudioRecorderComponent={renderAudioRecorderComponent}
            enableChatPanel={enableChatPanel}
            chatPanelRef={chatPanelRef}
            renderAIActionItems={renderAIActionItems}
            active={active}
            chatBarRef={chatBarRef}
            mode={mode}
            isPinMessageLoading={isPinMessageLoading}
            sendingStatus={sendingStatus}
            testId={testIdPrefix}
          />
        }
        onChange={value => {
          updateInputField({ ["label"]: value });
        }}
        onMentionActive={onMentionActive}
        options={collabs}
        onSend={onEnter}
        onClickMediaCard={onClickMediaCard}
        onCancelClick={cancelEditMessage}
        placeholder={inputTextPlaceHolder}
        focus={focus}
        onBlur={onBlur}
        replyContainer={replyContainer}
        sendOnEnter={sendOnEnter}
        containerStyle={containerStyle}
        chatPanelRef={chatPanelRef}
        {...chatBarV2Props}
        customChatContainer={
          <ChatPanel {...customChatbarProps} id="toddle-chat-panel" />
        }
        testId={testIdPrefix}
      />
      {showMediaModal && (
        <MediaModalWrapper
          attachments={selectedAttachment}
          toggleMediaModal={toggleMediaModal}
          activeIndex={selectedAttachmentIndex}
        />
      )}
      {showAccessibilityHelperText && isChatbarVisible && (
        <div
          className={classNames(
            "text-textSubtle",
            classes.accessibilityTextContainer,
            {
              invisible: alwaysShowHelperText
                ? !alwaysShowHelperText
                : !showHelperText,
            }
          )}
        >
          {sendOnEnter ? (
            <Trans i18nKey="designSystem:insert_line_hint">
              <b className={classes.accessibilityTextBold} />
            </Trans>
          ) : (
            <Trans i18nKey="designSystem:send_hint">
              <b className={classes.accessibilityTextBold} />
            </Trans>
          )}
        </div>
      )}
    </div>
  );
});

ChatbarWrapper.displayName = "ChatbarWrapper";

ChatbarWrapper.defaultProps = {
  isTaggingEnabled: false,
  isEmojiEnabled: true,
  emojiPickerProps: {},
  footerMsgContainerV2: false,
  onBlur: () => {},
  isStickyContainer: true,
  isAudioRecordingEnabled: true,
  shouldExecuteMessageDataUseEffect: true,
  allowInitialLabelValue: false,
  replyContainer: null,
  getAttachmentPopupContainer: null,
  renderAIActionItems: () => null,
  containerStyle: {},
  enableChatPanel: false,
  chatPanelRef: null,
  prompts: [],
  onExternalOptionClick: _.noop,
  plugins: {},
  translateLanguages: [],
  // keep the default value as null, parent component would explicity pass function if needed
  getAIResponseWrapper: null,
  metaData: {},
  saveDraftMessage: false,
  draftMessageStoreKey: "",
};

ChatbarWrapper.propTypes = {
  dsVersion: PropTypes.string,
  isTaggingEnabled: PropTypes.bool,
  isEmojiEnabled: PropTypes.bool,
  emojiPickerProps: PropTypes.object,
  footerMsgContainerV2: PropTypes.bool,
  isAudioRecordingEnabled: PropTypes.bool,
  shouldExecuteMessageDataUseEffect: PropTypes.bool,
  allowInitialLabelValue: PropTypes.bool,
  replyContainer: PropTypes.node,
  getAttachmentPopupContainer: PropTypes.func,
  renderAIActionItems: PropTypes.func,
  // following all are the enableChatPanel props
  enableChatPanel: PropTypes.bool,
  chatPanelRef: PropTypes.object, // required to ref the enableChatPanel, when boolean is true
  prompts: PropTypes.arrayOf(PropTypes.object),
  onExternalOptionClick: PropTypes.func,
  getAIResponseWrapper: PropTypes.func,
  plugins: PropTypes.object,
  translateLanguages: PropTypes.arrayOf(PropTypes.object),
  metaData: PropTypes.object,
  saveDraftMessage: PropTypes.bool,
  draftMessageStoreKey: PropTypes.string,
};

const mapStateToProps = (state, ownProps) => {
  const progressOfUploads = state.app_services.progressOfUploads;
  const defaultStudentNameOrderPreference = getDefaultStudentNameOrderPreference(
    state
  );
  const uploadFileType = _.get(
    _.values(progressOfUploads)[0],
    "attachment.type",
    ""
  );

  const isUploading = !_.isEmpty(progressOfUploads);
  const draftMessageStoreKey = _.get(
    ownProps,
    "draftMessageStoreKey",
    _.get(ownProps, "parentType", "")
  );

  let draftMessageData = _.get(
    state,
    `conversation.draftMessageData.${draftMessageStoreKey}`
  );
  if (_.isEmpty(draftMessageData)) {
    draftMessageData = getEmptyConversation();
  }

  return {
    isUploading,
    uploadFileType,
    defaultStudentNameOrderPreference,
    draftMessageStoreKey,
    draftMessageData,
  };
};

const mapDispatchToProps = { getAIResponse, setDraftMessageData };

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  I18nHOC({ resource: ["common", "classRoom", "designSystem"] })
)(ChatbarWrapper);
